/**
 * The Creos' small parts: the pills (in the composer and on a sent message),
 * the cards (new chat and Creos page), and the "/" list above a composer. A card is a
 * skill or an agent, shown by its own name and picture.
 */
import { createElement, useEffect, useLayoutEffect, useRef, type CSSProperties } from "react";
import { Ban, Check, Info, X } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { WorkshopQuickCreateItem, WorkshopQuickCreateRef } from "@/types/workshop.type";
import { kindIcon, kindLabel, pickId, unavailableNote } from "../quickCreate";
import { landCreoFlight } from "../creoFlight";

/** A skill or an agent: the mark of a card without a picture, and of each "/" row. */
function KindMark({ kind, className }: { kind: WorkshopQuickCreateItem["kind"]; className?: string }) {
  return createElement(kindIcon(kind), { className, "aria-hidden": true });
}

/** A small round picture, or the kind's mark when the card has none. */
function PillMark({ item, unavailable }: { item: WorkshopQuickCreateRef; unavailable?: boolean }) {
  if (unavailable) return <Ban className="ws-qc-pill-icon" aria-hidden="true" />;
  if (item.imageUrl) {
    return <span className="ws-qc-pill-thumb" style={{ backgroundImage: `url("${item.imageUrl}")` }} aria-hidden="true" />;
  }
  return <KindMark kind={item.kind} className="ws-qc-pill-icon" />;
}

/**
 * The card itself, never its prompt. In the composer it can be removed (the
 * words stay); grey with a slashed mark once it is no longer available.
 */
export function QuickCreatePill({
  item,
  onRemove,
  unavailable = false,
}: {
  item: WorkshopQuickCreateRef;
  onRemove?: () => void;
  unavailable?: boolean;
}) {
  const pillRef = useRef<HTMLSpanElement>(null);
  const id = pickId(item);
  // A pill that lands from Use it travels from the pop-up's name (M-QC2).
  useLayoutEffect(() => {
    if (onRemove && pillRef.current) landCreoFlight(id, pillRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only as it mounts
  }, []);
  return (
    <span
      ref={pillRef}
      className="ws-qc-pill"
      data-unavailable={unavailable || undefined}
      data-sent={!onRemove || undefined}
    >
      <PillMark item={item} unavailable={unavailable} />
      <span className="ws-qc-pill-label">{item.name}</span>
      {onRemove ? (
        <button type="button" className="ws-qc-pill-remove" onClick={onRemove} aria-label={`Remove ${item.name}`}>
          <X aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
}

/** Every pick a message carries, in a row that wraps. */
export function QuickCreatePills({
  items,
  onRemove,
  unavailable = [],
}: {
  items: WorkshopQuickCreateRef[];
  onRemove?: (item: WorkshopQuickCreateRef) => void;
  unavailable?: WorkshopQuickCreateRef[];
}) {
  if (items.length === 0) return null;
  const gone = new Set(unavailable.map(pickId));
  return (
    <span className="ws-qc-pills">
      {items.map((item) => (
        <QuickCreatePill
          key={pickId(item)}
          item={item}
          onRemove={onRemove ? () => onRemove(item) : undefined}
          unavailable={gone.has(pickId(item))}
        />
      ))}
    </span>
  );
}

/**
 * A Creo card: the picture, the name, and how it runs (or, once picked, In
 * your prompt with a check). `row` is the new chat's small card, `page` the
 * Creos page's tall one. The card adds itself to the prompt; ⓘ opens its
 * pop-up.
 */
export function CreoCard({
  item,
  variant,
  picked = false,
  order,
  disabled,
  onPick,
  onInfo,
}: {
  item: WorkshopQuickCreateItem;
  variant: "row" | "page";
  picked?: boolean;
  /** Its place in a stream that opens, for the stagger (See all). */
  order?: number;
  disabled?: boolean;
  onPick: () => void;
  /** Opens the pop-up. */
  onInfo: (card: HTMLElement | null) => void;
}) {
  return (
    <li
      className="ws-creo"
      data-variant={variant}
      data-picked={picked || undefined}
      style={order === undefined ? undefined : ({ "--i": order } as CSSProperties)}
    >
      <button
        type="button"
        className="ws-creo-main"
        onClick={onPick}
        disabled={disabled}
        aria-pressed={variant === "row" ? picked : undefined}
      >
        <QuickCreatePicture item={item} className="ws-creo-pic" />
        <span className="ws-creo-name">{item.name}</span>
      </button>
      <div className="ws-creo-foot">
        {picked ? (
          <span className="ws-creo-kind ws-creo-kind--picked">
            <Check aria-hidden="true" />
            In your prompt
          </span>
        ) : (
          <span className="ws-creo-kind">{kindLabel(item.kind)}</span>
        )}
        <button
          type="button"
          className="ws-creo-info"
          onClick={(e) => onInfo(e.currentTarget.closest<HTMLElement>(".ws-creo"))}
          aria-label={`About ${item.name}`}
        >
          <Info aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}

/** The card's picture, or the kind's mark on a plate when it has none. */
export function QuickCreatePicture({ item, className }: { item: WorkshopQuickCreateItem; className: string }) {
  if (item.imageUrl) {
    return <span className={className} style={{ backgroundImage: `url("${item.imageUrl}")` }} aria-hidden="true" />;
  }
  return (
    <span className={cn(className, "ws-qc-pic-none")} aria-hidden="true">
      <KindMark kind={item.kind} />
    </span>
  );
}

/** A loading card holds its place: a plate, a bar and the foot. */
export function CreoCardSkeleton({ variant }: { variant: "row" | "page" }) {
  return (
    <li className="ws-creo" data-variant={variant} aria-hidden="true">
      <span className="ws-creo-main">
        <span className="ws-creo-pic ws-skel" />
        <span className="ws-skel ws-skel--line" style={{ width: "70%" }} />
      </span>
      <div className="ws-creo-foot">
        <span className="ws-skel ws-skel--line" style={{ width: "45%" }} />
      </div>
    </li>
  );
}

/**
 * "/" in a composer: the Creos narrowed as you type, each row marked by its
 * kind (a skill or an agent). Several can be picked: a pick goes into the
 * message as a pill and "/" again adds the next. Picked rows show a check and
 * a second pick takes them out. Nothing matching says so in one muted line,
 * and Enter then sends the words as a normal message.
 */
export function QuickCreateSlashMenu({
  matches,
  picked,
  active,
  onHover,
  onPick,
}: {
  matches: WorkshopQuickCreateItem[];
  /** Ids (pickId) of the cards already in the message. */
  picked: ReadonlySet<string>;
  active: number;
  onHover: (index: number) => void;
  onPick: (item: WorkshopQuickCreateItem) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (matches.length === 0) {
    return (
      <div className="ws-qc-slash" role="status">
        <p className="ws-qc-slash-none">No Creo matches. Keep typing to send it as a message.</p>
      </div>
    );
  }
  return (
    <div className="ws-qc-slash">
      <ul
        ref={listRef}
        className="ws-qc-slash-list"
        role="listbox"
        aria-label="Creos"
        aria-multiselectable="true"
        id="ws-qc-slash"
      >
        {matches.map((item, index) => {
          const isPicked = picked.has(pickId(item));
          return (
            <li
              key={pickId(item)}
              id={`ws-qc-opt-${index}`}
              role="option"
              aria-selected={isPicked}
              data-active={index === active || undefined}
              data-index={index}
              className="ws-qc-slash-row"
              onMouseEnter={() => onHover(index)}
              // Keep the field's focus: the pick happens on the press.
              onMouseDown={(event) => {
                event.preventDefault();
                onPick(item);
              }}
            >
              <KindMark kind={item.kind} className="ws-qc-slash-icon" />
              <span className="ws-qc-slash-label">{item.name}</span>
              {isPicked ? <Check className="ws-qc-slash-icon" aria-hidden="true" /> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Above the composer when picks went while they sat in a draft. */
export function QuickCreateGone({ items }: { items: WorkshopQuickCreateRef[] }) {
  if (items.length === 0) return null;
  return (
    <p className="ws-qc-gone" role="alert">
      <Ban aria-hidden="true" />
      {unavailableNote(items.map((item) => item.name))}
    </p>
  );
}
